import { Suspense, lazy, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowRight, MapPin, Home as HomeIcon, HelpCircle, Phone } from 'lucide-react'
import MlsDisclaimer from '../components/listings/MlsDisclaimer'
import Section from '../components/layout/Section'
import Reveal from '../components/layout/Reveal'
import Seo from '../components/seo/Seo'
import { useBreadcrumbs } from '../seo/useBreadcrumbs'
import { placeSchema, faqSchema } from '../seo/schema'
import type { Area } from '../data/areas'

const LiveListingsMap = lazy(() => import('../components/listings/LiveListingsMap'))

/** Lazy-mount the Leaflet map when it scrolls near the viewport (P7 CWV). */
function LazyTownMap({ town }: { town: string }) {
  const ref = useRef<HTMLDivElement>(null)
  const [visible, setVisible] = useState(false)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setVisible(true)
          obs.disconnect()
        }
      },
      { rootMargin: '400px' },
    )
    obs.observe(el)
    return () => obs.disconnect()
  }, [])
  return (
    <div ref={ref} className="min-h-[480px]">
      {visible ? (
        <Suspense
          fallback={
            <div className="flex h-[480px] items-center justify-center rounded-lg border border-white/10 bg-white/5 text-white/60 text-sm uppercase tracking-[0.2em]">
              Loading map…
            </div>
          }
        >
          <LiveListingsMap initialTown={town} />
        </Suspense>
      ) : (
        <div className="flex h-[480px] items-center justify-center rounded-lg border border-white/10 bg-white/5 text-white/60 text-sm uppercase tracking-[0.2em]">
          Map loads as you scroll
        </div>
      )}
    </div>
  )
}

export default function AreaPage({ area }: { area: Area }) {
  const crumbs = useBreadcrumbs([
    { label: 'Areas', path: '/areas' },
    { label: area.name, path: `/areas/${area.slug}` },
  ])
  return (
    <div className="bg-neutral-charcoal">
      <Seo
        title={area.title}
        description={area.description}
        path={`/areas/${area.slug}`}
        ogImage={`https://debeachrealestate.com${area.heroImage}`}
        preloadImage={area.heroImage}
        jsonLd={[
          placeSchema({
            name: `${area.name}, Delaware`,
            latitude: area.geo.latitude,
            longitude: area.geo.longitude,
            description: area.description,
          }),
          crumbs.jsonLd,
          faqSchema(area.faq),
        ]}
      />

      {/* Hero */}
      <section className="relative h-[60vh] min-h-[420px] flex items-end overflow-hidden">
        <div className="absolute inset-0 bg-primary-navy/70 z-10" aria-hidden="true" />
        <img
          src={area.heroImage}
          srcSet={`${area.heroImage.replace('.jpg', '-768.jpg')} 768w, ${area.heroImage.replace('.jpg', '-1024.jpg')} 1024w, ${area.heroImage} 1280w`}
          sizes="100vw"
          alt={area.heroAlt}
          className="absolute inset-0 w-full h-full object-cover"
          loading="eager"
          fetchPriority="high"
        />
        <div className="relative z-20 w-full max-w-6xl mx-auto px-6 pb-14">
          {crumbs.node}
          <p className="font-display text-[12px] uppercase tracking-[0.3em] font-medium text-white/70">
            Delaware Beaches
            <span className="block w-6 h-[2px] bg-accent-gold mt-3" aria-hidden="true" />
          </p>
          <h1 className="mt-4 font-display uppercase text-white leading-none text-[clamp(36px,5.5vw,72px)]">
            {area.h1}
          </h1>
        </div>
      </section>

      {/* Intro */}
      <Section className="bg-neutral-charcoal">
        <Reveal>
          <div className="max-w-3xl space-y-6 text-lg text-white/70 font-light leading-[1.9]">
            {area.intro.map((para, i) => (
              <p key={i}>{para}</p>
            ))}
          </div>
        </Reveal>
      </Section>

      {/* Live listings map (town-filtered) */}
      <Section className="bg-neutral-charcoal pt-0">
        <Reveal>
          <div className="mb-8">
            <p className="font-display text-[12px] uppercase tracking-[0.3em] font-medium text-white/70">
              Live MLS
              <span className="block w-6 h-[2px] bg-accent-gold mt-3" aria-hidden="true" />
            </p>
            <h2 className="mt-4 font-display uppercase text-white leading-none text-[clamp(28px,4vw,48px)]">
              <span className="font-thin">Homes for Sale in </span>
              <span className="font-bold">{area.name}</span>
            </h2>
            <p className="mt-4 max-w-2xl text-white/60 font-light leading-relaxed">
              Every live Bright MLS listing in and around {area.name}, updated
              throughout the day. Pan the map to explore, or{' '}
              <Link to={`/listings?town=${area.mapTown}`} className="text-accent-gold hover:underline">
                open the full listings browser
              </Link>
              .
            </p>
          </div>
        </Reveal>
        <LazyTownMap town={area.mapTown} />
        <MlsDisclaimer />
      </Section>

      {/* Neighborhoods */}
      <Section className="bg-primary-ink">
        <Reveal>
          <div className="mb-10">
            <p className="font-display text-[12px] uppercase tracking-[0.3em] font-medium text-white/70">
              <MapPin className="inline h-3.5 w-3.5 mr-2 -mt-0.5 text-accent-gold" aria-hidden="true" />
              Where to Look
              <span className="block w-6 h-[2px] bg-accent-gold mt-3" aria-hidden="true" />
            </p>
            <h2 className="mt-4 font-display uppercase text-white leading-none text-[clamp(28px,4vw,48px)]">
              <span className="font-thin">{area.name} </span>
              <span className="font-bold">Neighborhoods & Communities</span>
            </h2>
          </div>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {area.neighborhoods.map((n) => (
            <Reveal key={n.name}>
              <div className="h-full border border-white/10 bg-white/5 p-6 hover:border-accent-gold/50 transition-colors">
                <h3 className="font-display text-lg font-bold text-white uppercase tracking-wide">
                  {n.name}
                </h3>
                <p className="mt-3 text-white/60 font-light leading-relaxed">{n.blurb}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* Lifestyle */}
      <Section className="bg-neutral-charcoal">
        <Reveal>
          <h2 className="font-display uppercase text-white leading-none text-[clamp(28px,4vw,48px)] mb-10">
            <span className="font-thin">Living in </span>
            <span className="font-bold">{area.name}</span>
          </h2>
        </Reveal>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {area.lifestyle.map((l) => (
            <Reveal key={l.heading}>
              <h3 className="font-display text-sm font-bold uppercase tracking-[0.2em] text-accent-gold mb-4">
                {l.heading}
              </h3>
              <p className="text-white/70 font-light leading-[1.9]">{l.body}</p>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* Buyer notes */}
      <Section className="bg-primary-ink">
        <Reveal>
          <div className="max-w-3xl">
            <h2 className="font-display uppercase text-white leading-none text-[clamp(28px,4vw,48px)] mb-8">
              <span className="font-thin">What Buyers Should Know About </span>
              <span className="font-bold">{area.name}</span>
            </h2>
            <ul className="space-y-4">
              {area.buyerNotes.map((note, i) => (
                <li key={i} className="flex gap-3 text-white/70 font-light leading-relaxed">
                  <HomeIcon className="h-4 w-4 mt-1.5 shrink-0 text-accent-gold" aria-hidden="true" />
                  <span>{note}</span>
                </li>
              ))}
            </ul>
          </div>
        </Reveal>
      </Section>

      {/* FAQ */}
      <Section className="bg-neutral-charcoal">
        <Reveal>
          <div className="max-w-3xl">
            <p className="font-display text-[12px] uppercase tracking-[0.3em] font-medium text-white/70 mb-4">
              <HelpCircle className="inline h-3.5 w-3.5 mr-2 -mt-0.5 text-accent-gold" aria-hidden="true" />
              {area.name} FAQs
            </p>
            <h2 className="font-display uppercase text-white leading-none text-[clamp(28px,4vw,48px)] mb-8">
              <span className="font-thin">Questions About </span>
              <span className="font-bold">{area.name} Real Estate</span>
            </h2>
            <div className="space-y-3">
              {area.faq.map((f) => (
                <details
                  key={f.question}
                  className="group border border-white/10 bg-white/5 open:border-accent-gold/40"
                >
                  <summary className="cursor-pointer list-none p-5 font-display text-sm font-bold uppercase tracking-wide text-white flex items-center justify-between gap-4 [&::-webkit-details-marker]:hidden">
                    {f.question}
                    <span className="text-accent-gold text-xl leading-none transition-transform group-open:rotate-45" aria-hidden="true">
                      +
                    </span>
                  </summary>
                  <p className="px-5 pb-5 text-white/70 font-light leading-[1.9]">{f.answer}</p>
                </details>
              ))}
            </div>
          </div>
        </Reveal>
      </Section>

      {/* CTA band */}
      <section className="bg-accent-gold py-16">
        <div className="max-w-6xl mx-auto px-6 flex flex-col md:flex-row md:items-center md:justify-between gap-8">
          <div>
            <h2 className="font-display uppercase text-primary-ink leading-none text-[clamp(26px,3.5vw,44px)]">
              <span className="font-thin">Thinking about </span>
              <span className="font-bold">{area.name}?</span>
            </h2>
            <p className="mt-3 text-primary-ink/80 max-w-xl">
              Talk to Shawn & Merritt — we live and work here, and we&apos;ll give
              you the straight story on any street, building, or community.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 shrink-0">
            <Link
              to="/contact?subject=BUYING"
              className="inline-flex items-center justify-center gap-2 bg-primary-ink text-white px-8 py-4 text-sm font-display font-bold uppercase tracking-[0.15em] hover:bg-neutral-charcoal transition-colors"
            >
              Start the Conversation <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
            <a
              href="tel:+13025428591"
              className="inline-flex items-center justify-center gap-2 border-2 border-primary-ink text-primary-ink px-8 py-4 text-sm font-display font-bold uppercase tracking-[0.15em] hover:bg-primary-ink hover:text-white transition-colors"
            >
              <Phone className="h-4 w-4" aria-hidden="true" /> 302-542-8591
            </a>
          </div>
        </div>
      </section>
    </div>
  )
}
